import { MOMENT_TYPES } from 'utils/constants';

/**
 * RTM phase (VIP, ETLA, …) is a display-only prefix on phased offer cards.
 * API `name` must be the storage title without "{phase}:".
 */

export interface ReleasePhase {
  id: string;
  phaseName: string;
}

/** API / timeline payloads may omit id or phaseName on individual entries. */
export interface ReleasePhaseLike {
  readonly id?: string | null;
  readonly phaseName?: string | null;
}

export interface RtmPhasedTitleParts {
  phaseLabel: string;
  storageTitle: string;
  displayTitle: string;
  isPhasedOffer: boolean;
}

export function stripRtmPhaseNamePrefix(
  name: string | undefined | null,
  phaseName: string | undefined | null
): string {
  if (name == null) return '';
  const n = String(name).trim();
  if (!phaseName?.trim()) return n;
  const esc = phaseName.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Use + quantifier to strip all repeated prefixes in one pass (e.g. "VIP: VIP: title")
  const re = new RegExp(`^(?:${esc}:\\s*)+`, 'i');
  return n.replace(re, '').trim();
}

export function withRtmPhaseDisplayTitle(
  phaseName: string | undefined | null,
  storageTitle: string | undefined | null
): string {
  const base = stripRtmPhaseNamePrefix(storageTitle ?? '', phaseName).trim();
  return phaseName?.trim() ? `${phaseName.trim()}: ${base}` : base;
}

/** Same rules as timeline cards: PC&O always; Price Changes and Offers only when `releasePhase` is set. */
export function phasedOfferUsesPhaseTitle(
  momentType: string | undefined | null,
  releasePhase: string | undefined | null
): boolean {
  if (momentType === MOMENT_TYPES.PRICE_CHANGES_AND_OFFERS) return true;
  return momentType === MOMENT_TYPES.PROMOS_OFFERS && !!releasePhase?.trim();
}

export function resolvePhaseDisplayName(input: {
  readonly releasePhase?: string | null;
  readonly releasePhaseId?: string | null;
  readonly releasePhases?: readonly ReleasePhaseLike[] | null;
}): string {
  let phase = input.releasePhase?.trim() ?? '';
  const arr = input.releasePhases;
  if (Array.isArray(arr) && arr.length) {
    const match = input.releasePhaseId
      ? arr.find(p => p.id === input.releasePhaseId)
      : phase
      ? arr.find(p => p.phaseName === phase)
      : null;
    if (match?.phaseName) phase = String(match.phaseName).trim();
  }
  return phase;
}

/**
 * One call for timeline + slideout: storage title for PATCH, display title for UI.
 */
export function getRtmPhasedTitleParts(input: {
  readonly momentType?: string | null;
  readonly rawTitle: string;
  readonly releasePhase?: string | null;
  readonly releasePhaseId?: string | null;
  readonly releasePhases?: readonly ReleasePhaseLike[] | null;
}): RtmPhasedTitleParts {
  const { momentType, rawTitle, releasePhase, releasePhaseId, releasePhases } =
    input;

  if (!phasedOfferUsesPhaseTitle(momentType, releasePhase)) {
    return {
      phaseLabel: '',
      storageTitle: rawTitle,
      displayTitle: rawTitle,
      isPhasedOffer: false
    };
  }

  const phaseLabel = resolvePhaseDisplayName({
    releasePhase,
    releasePhaseId,
    releasePhases
  });

  if (!phaseLabel) {
    return {
      phaseLabel: '',
      storageTitle: rawTitle,
      displayTitle: rawTitle,
      isPhasedOffer: true
    };
  }

  const storageTitle = stripRtmPhaseNamePrefix(rawTitle, phaseLabel);
  return {
    phaseLabel,
    storageTitle,
    displayTitle: withRtmPhaseDisplayTitle(phaseLabel, storageTitle),
    isPhasedOffer: true
  };
}

/** Phase string for Header strip only (no raw title needed). */
export function getPhasedOfferPhaseStripLabel(input: {
  readonly momentType?: string | null;
  readonly releasePhase?: string | null;
  readonly releasePhaseId?: string | null;
  readonly releasePhases?: readonly ReleasePhaseLike[] | null;
}): string | undefined {
  if (!phasedOfferUsesPhaseTitle(input.momentType, input.releasePhase)) {
    return undefined;
  }
  const label = resolvePhaseDisplayName(input);
  return label || undefined;
}
